import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeQueries,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { Smartphone } from "lucide-react";
import { formatName, formatPhone } from "@tellescope/utils";
import { useMemo, useState } from "react";
import { cn, ReversePaginatedList } from "@tellescope/ui";
import { ChatAttachment, MessageBubbleWithSecureAvatar } from "../components/MessageBubbleWithSecureAvatar";
import { SharedPhoneNumberToggle } from "../components/SharedPhoneNumberToggle";

export interface SMSContextCardProps {
  smsId: string;
  className?: string;
}

export const SMSContextCard = ({ smsId, className }: SMSContextCardProps) => {
  const { t } = useTranslation("tickets");
  const [filterByEnduserId, setFilterByEnduserId] = useState(true);

  // Get the context SMS message
  const smsQuery = useTellescopeQuery({
    model: "sms_messages",
    method: "getOne",
    request: smsId,
    enabled: !!smsId,
  });

  // Get SMS thread (25 messages before + context message)
  const threadQuery = useTellescopeInfiniteQuery({
    model: "sms_messages",
    method: "getSome",
    request: {
      to: smsQuery.data?.createdAt ? new Date(smsQuery.data?.createdAt.getTime() + 1000) : undefined,
      mdbFilter: {
        phoneNumber: { $eq: smsQuery.data?.phoneNumber ?? "" },
        enduserPhoneNumber: { $eq: smsQuery.data?.enduserPhoneNumber ?? "" },
        enduserId: filterByEnduserId ? { $eq: smsQuery.data?.enduserId ?? "" } : undefined,
        isDraft: { $ne: true },
        delivered: { $eq: true },
      },
      limit: 25,
    },
    enabled: !!smsQuery.data,
    select: (data) => data.pages.flat().reverse(),
  });

  // Get unique user and enduser IDs from messages
  const { userIds, enduserIds } = useMemo(() => {
    const messages = threadQuery.data?.flat() ?? [];
    const userIds = new Set<string>();
    const enduserIds = new Set<string>();

    messages.forEach((msg) => {
      if (msg.userId) userIds.add(msg.userId);
      if (msg.enduserId) enduserIds.add(msg.enduserId);
    });

    return {
      userIds: Array.from(userIds),
      enduserIds: Array.from(enduserIds),
    };
  }, [threadQuery.data]);

  // Query users
  const usersMap = useTellescopeQueries({
    model: "users",
    method: "getOne",
    requests: userIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"users">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: userIds.length > 0,
  });

  // Query endusers
  const endusersMap = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: enduserIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"endusers">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: enduserIds.length > 0,
  });

  const { senderName, recipientName } = useMemo(() => {
    const enduser = smsQuery.data?.enduserId ? endusersMap[smsQuery.data.enduserId] : undefined;
    const user = smsQuery.data?.userId ? usersMap[smsQuery.data?.userId] : undefined;
    const enduserName = enduser ? formatName(enduser) : t("Unknown Patient");
    const enduserPhone = smsQuery.data?.enduserPhoneNumber ? formatPhone(smsQuery.data?.enduserPhoneNumber) : "";
    const userName = user ? formatName(user) : t("Unknown User");
    const userPhone = smsQuery.data?.phoneNumber ? formatPhone(smsQuery.data?.phoneNumber) : "";

    if (smsQuery.data?.inbound) {
      return {
        senderName: filterByEnduserId
          ? `${enduserName} ${enduserPhone}`.trim()
          : t("all patients with {{enduserPhone}}", { enduserPhone }),
        recipientName: `${userName} ${userPhone}`.trim(),
      };
    }
    return {
      senderName: `${userName} ${userPhone}`.trim(),
      recipientName: filterByEnduserId
        ? `${enduserName} ${enduserPhone}`.trim()
        : t("all patients with {{enduserPhone}}", { enduserPhone }),
    };
  }, [
    endusersMap,
    filterByEnduserId,
    smsQuery.data?.enduserId,
    smsQuery.data?.enduserPhoneNumber,
    smsQuery.data?.inbound,
    smsQuery.data?.phoneNumber,
    smsQuery.data?.userId,
    t,
    usersMap,
  ]);

  const enduserName = useMemo(() => {
    if (!smsQuery.data) return "";
    const enduser = endusersMap[smsQuery.data?.enduserId];

    const name = enduser ? formatName(enduser) : undefined;
    return name || t("Unknown Patient");
  }, [endusersMap, smsQuery.data, t]);

  if (smsQuery.isLoading || threadQuery.isLoading || !threadQuery.data?.length) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading SMS thread...")}</p>
      </div>
    );
  }

  if (smsQuery.isError || threadQuery.isError || !smsQuery.data) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load SMS thread. Please try again.")}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2 p-4 border border-gray-200 rounded-lg bg-white", className)}>
      <SharedPhoneNumberToggle
        phoneNumber={smsQuery.data.enduserPhoneNumber ?? ""}
        contactName={enduserName}
        showAll={!filterByEnduserId}
        onToggle={(newVal) => setFilterByEnduserId(!newVal)}
        className="mb-2"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Smartphone className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("SMS")}</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("{{senderName}} to {{recipientName}}", { senderName, recipientName })}
        </p>
      </div>

      <div className="overflow-hidden border border-gray-200 rounded-lg max-h-[400px]">
        <ReversePaginatedList className="max-h-[400px] p-4" onTopReached={() => threadQuery.fetchNextPage()}>
          {threadQuery.data.map((message) => {
            const sender = message.inbound ? endusersMap[message.creator] : usersMap[message.creator];

            return (
              <MessageBubbleWithSecureAvatar
                key={message.id}
                content={message.templatedMessage ?? message.message}
                timestamp={
                  message.sendAt && !message.delivered
                    ? message.sendAt ?? message.timestamp
                    : message.timestamp ?? message.createdAt
                }
                isOutbound={!message.inbound}
                sender={
                  sender
                    ? {
                        id: sender.id,
                        type: message.inbound ? "enduser" : "user",
                        name: formatName(sender),
                        avatar: sender.avatar,
                      }
                    : {
                        id: "",
                        type: "user",
                        name: t("Unknown"),
                      }
                }
                status={
                  message.error
                    ? {
                        type: "error",
                        message: message.error,
                      }
                    : !message.delivered
                    ? {
                        type: "sending",
                        message: t("Sending..."),
                      }
                    : message.sendAt
                    ? { type: "scheduled" }
                    : undefined
                }
                chatAttachments={message.images?.map<ChatAttachment>((img) => ({
                  type: img.type,
                  secureName: img.url,
                }))}
              />
            );
          })}
        </ReversePaginatedList>
      </div>
    </div>
  );
};
